import {format, isValid, parse} from "date-fns";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";

const STATUS_BACKGROUNDS = {
  ONLINE: "#808080",
  DISPATCH: "#008C51",
  DISPATCH_ACTIVITY: "#287499",
  OFFLINE: "repeating-linear-gradient(45deg, #4B5563, #4B5563 4px, #DC2626 4px, #DC2626 5px)",
  NOSIGN: "repeating-linear-gradient(45deg, #EAEAEA, #EAEAEA 4px, #B3B3B3 4px, #B3B3B3 5px)",
  SHIFT_BASE: "#FFF0C5"
};

const GarrisonTimelinePrint = ({data, filter, zoom = {start: 0, end: 100}}) => {
  const parseFilterDate = (value) => {
    const parsedDate = parse(value, "dd/MM/yyyy HH:mm", new Date());

    return isValid(parsedDate) ? parsedDate : new Date(value);
  };

  const dtStart = filter?.dtStart || new Date().toISOString();
  const baseStart = parseFilterDate(dtStart);
  const baseEnd = filter?.dtEnd
    ? parseFilterDate(filter.dtEnd)
    : new Date(baseStart.getTime() + 86400000);
  const totalSeconds = Math.max(0, Math.floor((baseEnd.getTime() - baseStart.getTime()) / 1000));
  const visibleStartSeconds = Math.floor((totalSeconds * zoom.start) / 100);
  const visibleEndSeconds = Math.floor((totalSeconds * zoom.end) / 100);
  const visibleTotalSeconds = Math.max(0, visibleEndSeconds - visibleStartSeconds);
  const rulerMarks = Array.from({length: 9}, (_, index) => Math.floor((visibleTotalSeconds / 8) * index));
  const garrisons = data?.garrisons ?? [];
  const dispatchesById = new Map(
    (data?.dispatches || []).map(({dispatchId, dispatchActivity, dispatchNature}) => [
      String(dispatchId),
      {dispatchActivity, dispatchNature}
    ])
  );
  const getSegmentStatus = (segment) => {
    if (["OFFLINE", "NOSIGN"].includes(segment.status)) return segment.status;

    const dispatch = segment.dispatchId ? dispatchesById.get(String(segment.dispatchId)) : null;
    const isActivity = dispatch?.dispatchActivity || dispatch?.dispatchNature?.activity || String(dispatch?.dispatchNature || "").toUpperCase() === "ATIVIDADE";

    if (segment.dispatchId) return isActivity ? "DISPATCH_ACTIVITY" : "DISPATCH";

    return "ONLINE";
  };

  const getVisibleDate = (seconds) => new Date(baseStart.getTime() + (visibleStartSeconds + seconds) * 1000);
  const getSegmentStyle = (startSeconds, endSeconds) => {
    if (visibleTotalSeconds <= 0) return null;

    const start = Math.max(startSeconds, visibleStartSeconds);
    const end = Math.min(endSeconds, visibleEndSeconds);

    if (end <= start) return null;

    return {
      left: `${((start - visibleStartSeconds) / visibleTotalSeconds) * 100}%`,
      width: `${((end - start) / visibleTotalSeconds) * 100}%`
    };
  };

  return (
    <table className="w-full table-fixed border-collapse text-[11px]">
      <colgroup>
        <col className="w-[240px]" />
        <col />
      </colgroup>
      <thead>
        <tr className="border-b border-[#E2E8F0]">
          <th className="px-2 py-1 text-left font-semibold">Guarnição</th>
          <th className="h-8 p-0 text-left font-normal">
            <div className="relative h-8 w-full">
              {rulerMarks.map((mark, index) => {
                const percent = visibleTotalSeconds > 0 ? (mark / visibleTotalSeconds) * 100 : 0;

                return (
                  <span
                    key={index}
                    className="absolute bottom-1 -translate-x-1/2 text-[10px] text-gray-500"
                    style={{left: `${percent}%`}}
                  >
                    {format(getVisibleDate(mark), "HH:mm")}
                  </span>
                );
              })}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {garrisons.map((garrison, index) => {
          const segments = data?.ganttSegments?.[garrison.id] ?? [];
          const shiftStyle = getSegmentStyle(
            (garrison.shiftStartMinute || 0) * 60,
            (garrison.shiftEndMinute || 0) * 60
          );

          const dispatchGroups = {};
          segments.forEach((segment) => {
            if (segment.dispatchId) {
              if (!dispatchGroups[segment.dispatchId]) {
                dispatchGroups[segment.dispatchId] = {start: segment.startMinute, end: segment.endMinute, count: 1, color: STATUS_BACKGROUNDS[getSegmentStatus(segment)]};
              } else {
                dispatchGroups[segment.dispatchId].start = Math.min(dispatchGroups[segment.dispatchId].start, segment.startMinute);
                dispatchGroups[segment.dispatchId].end = Math.max(dispatchGroups[segment.dispatchId].end, segment.endMinute);
                dispatchGroups[segment.dispatchId].count += 1;
              }
            }
          });

          const iconPath = getAssetPath("/img/garrison-timeline/icon.svg");
          const garrisonIndex = (garrison.originalIndex ?? ((data?.page || 0) * (data?.pageSize || garrisons.length) + index)) + 1;

          return (
            <tr key={garrison.id} className="break-inside-avoid border-b border-[#E2E8F0]">
              <td className="px-2 py-1 align-middle">
                <div className="flex items-center gap-2">
                  <div className="relative h-8 w-8 shrink-0">
                    <img src={iconPath} className="w-full h-full block" alt="Ícone de Guarnição" />
                    <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white flex items-center justify-center text-[10px] leading-none font-bold text-[#063a6c]">
                      {garrisonIndex}
                    </div>
                  </div>
                  <span className="truncate font-medium">{garrison.name}</span>
                </div>
              </td>
              <td className="h-10 overflow-hidden p-0">
                <div className="relative h-10 w-full overflow-hidden">
                  {shiftStyle && (
                    <div
                      className="absolute top-1/2 h-6 -translate-y-1/2 rounded-full"
                      style={{...shiftStyle, background: STATUS_BACKGROUNDS.SHIFT_BASE}}
                    />
                  )}
                  {Object.entries(dispatchGroups).map(([dispatchId, group]) => {
                    if (group.count <= 1) return null;
                    const boxStyle = getSegmentStyle(group.start * 60, group.end * 60);
                    if (!boxStyle) return null;

                    return (
                      <div
                        key={`parent-${garrison.id}-${dispatchId}`}
                        className="absolute top-1/2 h-4 -translate-y-1/2 rounded-full border-2 border-solid"
                        style={{...boxStyle, background: "transparent", borderColor: group.color}}
                      />
                    );
                  })}
                  {segments.map((segment, index) => {
                    const style = getSegmentStyle(segment.startMinute * 60, segment.endMinute * 60);
                    if (!style) return null;

                    const segmentStatus = getSegmentStatus(segment);
                    const groupedDispatchId = segmentStatus === "NOSIGN"
                      ? Object.entries(dispatchGroups).find(([, group]) => group.count > 1 && segment.startMinute >= group.start && segment.endMinute <= group.end)?.[0] || null
                      : null;
                    const dispatchId = segment.dispatchId || groupedDispatchId;
                    const isChild = ["DISPATCH", "DISPATCH_ACTIVITY", "NOSIGN"].includes(segmentStatus)
                      && dispatchGroups[dispatchId]?.count > 1;

                    return (
                      <div
                        key={`${garrison.id}-${index}`}
                        className={`absolute top-1/2 ${isChild ? "h-3" : "h-4"} -translate-y-1/2 rounded-full overflow-hidden`}
                        style={{...style, background: STATUS_BACKGROUNDS[segmentStatus] || "#000"}}
                      />
                    );
                  })}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default GarrisonTimelinePrint;
